import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { toMonitoringType } from 'shared/enums/fault-type';
import { deviceDisplayType } from 'shared/utils/device-utils';

import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import useLinkableDevicesTable from 'features/device/linkable-device/views/hooks';
import styles from 'features/device/linkable-device/views/linkable-devices-table.module.scss';
import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';
import DeviceFilters from 'features/device/views/device-filters';

type Props = {
  getErrorMessageForDevice?: (device: LinkableDevice) => string | undefined;
};

const LinkableDevicesTable = ({ getErrorMessageForDevice }: Props): React.JSX.Element => {
  const { t } = useTranslation('linkableDevice');
  const { t: deviceTypesTranslations } = useTranslation('deviceTypes');
  const { handleSelectedDevicesChanged } = useDeviceLinkContextProvider();

  const renderIcon = (device: LinkableDevice): ReactElement => {
    const monitoringType: MonitoringType | undefined = toMonitoringType(device.fault);

    return monitoringType ? <MonitoringIcon type={monitoringType} withTooltip visible size={16} /> : <></>;
  };

  const columns: ColumnDefinition<LinkableDevice, NestedKeyof<LinkableDevice>>[] = [
    {
      key: 'name',
      label: t('linkableDevicesTable.column.name'),
      tableCellProps: {
        width: 200,
      },
    },
    {
      key: 'type',
      label: t('linkableDevicesTable.column.type'),
      renderCustomContentProvider: (device) => <>{deviceDisplayType(deviceTypesTranslations, device)}</>,
      disableSort: true,
      tableCellProps: {
        width: 200,
      },
    },
    {
      key: 'gateway.name',
      label: t('linkableDevicesTable.column.gateway'),
      renderCustomContentProvider: ({ gateway, system }) => <>{gateway ? gateway.name : system.name}</>,
      tableCellProps: {
        width: 200,
      },
    },
    {
      key: 'source',
      label: t('linkableDevicesTable.column.source'),
      disableSort: true,
      tableCellProps: {
        width: 200,
      },
    },
    {
      key: 'fault',
      label: '',
      renderCustomContentProvider: renderIcon,
      disableSort: true,
      tableCellProps: {
        align: 'left',
        width: 40,
      },
    },
  ];

  const {
    readLinkableDevicesData,
    readLinkableDevicesIsLoading,
    filterValues,
    currentFilters,
    search,
    sort,
    currentPage,
    currentRowsPerPage,
    handleOnSearchChanged,
    setSearch,
    handleOnOptionsSelected,
    clearFilters,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSortChanged,
  } = useLinkableDevicesTable();

  const hiddenAndLiveDataMergedList = readLinkableDevicesData?.devices ?? [];

  return (
    <>
      <DeviceFilters
        onSearchValueChanged={(value: string) => {
          handleOnSearchChanged(value);
          setSearch(value);
        }}
        activeFilters={currentFilters}
        monitoringValues={[]}
        filterValues={filterValues}
        activeMonitoringFilters={[]}
        onMonitoringFilterSelected={() => {}}
        onOptionsSelected={handleOnOptionsSelected}
        clearFilters={clearFilters}
        searchBarValue={search}
      />
      {readLinkableDevicesIsLoading && <LoadingIndicator />}
      {!readLinkableDevicesIsLoading && readLinkableDevicesData && (
        <Table
          className={styles.linkableDevicesTable}
          data={hiddenAndLiveDataMergedList.map<RowItem<LinkableDevice>>((linkableDevice) => ({
            data: linkableDevice,
          }))}
          columns={columns}
          rowsAreSelectable
          onSelectedRowsChanged={(devices: LinkableDevice[]) => handleSelectedDevicesChanged(devices)}
          getErrorMessageForRow={getErrorMessageForDevice}
          pagination={{
            count: readLinkableDevicesData.total,
            onPageChanged: handleOnPageChanged,
            rowsPerPage: currentRowsPerPage,
            page: currentPage,
            onRowsPerPageChanged: handleOnRowsPerPageChanged,
          }}
          onSortChanged={handleOnSortChanged}
          rowIdentifier={(item: LinkableDevice) => item.id}
          initialOrderBy={sort.property}
          initialOrderDirection={sort.isAscending ? 'asc' : 'desc'}
        />
      )}
    </>
  );
};

export default LinkableDevicesTable;
